'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  FilteredCounter,
  ListPageFilterPanel,
  UnverifiedAssetStatusFilter,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import {
  ASSETS_VIEW,
  ASSET_TYPE,
  supportsCodeGraphFilter,
  useTabScopedAssetsFilters,
} from '../../../store/tab/assetsTabScopedFilterStore';
import { useTabScopedDomainTree } from '../../../store/tab/domainTreeTabScopedFilterStore';
import { CreatedSinceFilterPill } from '../../common/CreatedSinceFilterPill';
import { ListPageDateRangeFilter } from '../../common/ListPageDateRangeFilter';
import { AssetsAdvancedFilters } from '../AssetsAdvancedFilters';
import { AssetsMethodFilter } from './AssetsMethodFilter';
import { AssetsPathFilter } from './AssetsPathFilter';
import { AssetsApiTypeFilter } from './AssetsApiTypeFilter';

type AssetsFilterPanelProps = {
  selection?: ReactNode;
  filteredCount: number;
  showWithCodeGraphFilter: boolean;
};

export function AssetsFilterPanel({
  selection,
  filteredCount,
  showWithCodeGraphFilter,
}: AssetsFilterPanelProps) {
  const t = useTranslations('proxy.filteredCounter.entities');
  const tFilters = useTranslations('proxy.filters');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const filters = useTabScopedAssetsFilters();
  const { selectedNodes, setSelectedNodes } = useTabScopedDomainTree();
  const isEndpointView = filters.view !== ASSETS_VIEW.WEB_PAGES;
  const canFilterByCodeGraph =
    showWithCodeGraphFilter && supportsCodeGraphFilter(filters.typeFilter);
  const hasAdvancedFilters =
    !!filters.updatedStartDate ||
    !!filters.updatedEndDate ||
    (canFilterByCodeGraph && filters.withCodeGraph);
  const hasActiveFilters =
    hasAdvancedFilters ||
    !!filters.createdStartDate ||
    !!filters.createdEndDate ||
    filters.createdSince !== null ||
    filters.searchTerm !== '' ||
    filters.methodsFilter.length > 0 ||
    filters.apiTypes.length > 0 ||
    filters.unverifiedStatuses.length > 0 ||
    selectedNodes.length > 0;
  const isAdvancedExpanded = isAdvancedOpen || hasAdvancedFilters;

  return (
    <VStack gap="3" alignItems="stretch">
      <ListPageFilterPanel
        selection={selection}
        isClearDisabled={!hasActiveFilters}
        onClear={() => {
          filters.resetFiltersForView(filters.view);
          setSelectedNodes([]);
        }}
        addFilter={{
          isActive: isAdvancedExpanded,
          onClick: () => setIsAdvancedOpen((isOpen) => !isOpen),
        }}
        trailing={
          hasActiveFilters ? (
            <FilteredCounter
              filteredCount={filteredCount}
              entityLabel={t('assets')}
            />
          ) : null
        }
        expandedContent={
          isAdvancedExpanded ? (
            <AssetsAdvancedFilters
              updatedStartDate={filters.updatedStartDate}
              setUpdatedStartDate={filters.setUpdatedStartDate}
              updatedEndDate={filters.updatedEndDate}
              setUpdatedEndDate={filters.setUpdatedEndDate}
              showWithCodeGraphFilter={canFilterByCodeGraph}
              withCodeGraph={filters.withCodeGraph}
              setWithCodeGraph={filters.setWithCodeGraph}
            />
          ) : null
        }
      >
        {filters.view !== ASSETS_VIEW.UNVERIFIED ? (
          <AssetsPathFilter
            value={selectedNodes}
            onValueChange={setSelectedNodes}
          />
        ) : null}
        {filters.view === ASSETS_VIEW.ENDPOINTS ? (
          <AssetsApiTypeFilter
            value={filters.apiTypes}
            onValueChange={filters.setApiTypes}
          />
        ) : null}
        {isEndpointView ? (
          <AssetsMethodFilter
            value={filters.methodsFilter}
            onValueChange={filters.setMethodsFilter}
            disabled={filters.typeFilter === ASSET_TYPE.AGENTS}
          />
        ) : null}
        {filters.view === ASSETS_VIEW.UNVERIFIED ? (
          <UnverifiedAssetStatusFilter
            value={filters.unverifiedStatuses}
            onValueChange={filters.setUnverifiedStatuses}
          />
        ) : null}
        <CreatedSinceFilterPill
          createdSince={filters.createdSince}
          onClear={() => filters.setCreatedSince(null)}
        />
        <ListPageDateRangeFilter
          label={tFilters('createdAt')}
          startDate={filters.createdStartDate}
          endDate={filters.createdEndDate}
          onStartDateChange={filters.setCreatedStartDate}
          onEndDateChange={filters.setCreatedEndDate}
        />
      </ListPageFilterPanel>
    </VStack>
  );
}
